import { useCallback, useMemo, useState, type ReactNode } from "react";
import { cn } from "../cn";
import { ToastContext, type Toast, type ToastTone } from "./toast-context";

const SHOWN_MS = 3000;
const LEAVE_MS = 200;

/**
 * Wraps the app so any component can raise a toast.
 *
 * `look="compact"` sets a toast in `text-ui` on a 1.35 line under a floating
 * shadow, with a quiet × in `text-prose` whose hit area is wider than the mark.
 * Opt-in; `default` is the console's.
 */
export function ToastProvider({
  children,
  look = "default",
}: {
  children: ReactNode;
  look?: "default" | "compact";
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [leaving, setLeaving] = useState<ReadonlySet<number>>(new Set());

  // Out is 200ms: it fades, then the gap it leaves closes over the same 200.
  const dismiss = useCallback((id: number) => {
    setLeaving((current) => new Set(current).add(id));
    setTimeout(() => {
      setToasts((current) => current.filter((t) => t.id !== id));
      setLeaving((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }, LEAVE_MS);
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, "id">) => {
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { ...toast, id }]);
      // Errors stay until dismissed. A failure that vanishes after five seconds
      // is a failure the person never read.
      if (toast.tone !== "critical") {
        setTimeout(() => {
          dismiss(id);
        }, SHOWN_MS);
      }
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport
        toasts={toasts}
        leaving={leaving}
        onDismiss={dismiss}
        compact={look === "compact"}
      />
    </ToastContext.Provider>
  );
}

const toneClass: Record<ToastTone, string> = {
  neutral: "border-rule bg-surface text-ink",
  positive: "border-positive bg-positive-soft text-ink",
  warning: "border-warning bg-warning-soft text-ink",
  critical: "border-critical bg-critical-soft text-ink",
};

function ToastViewport({
  toasts,
  leaving,
  onDismiss,
  compact,
}: {
  toasts: Toast[];
  leaving: ReadonlySet<number>;
  onDismiss: (id: number) => void;
  compact: boolean;
}) {
  const top = toasts.filter((t) => t.placement === "top");
  return (
    <>
      <ToastStack
        toasts={toasts.filter((t) => t.placement !== "top")}
        leaving={leaving}
        onDismiss={onDismiss}
        compact={compact}
        position="right-4 bottom-4"
      />
      <ToastStack
        toasts={top}
        leaving={leaving}
        onDismiss={onDismiss}
        compact={compact}
        position="top-[72px] right-4"
      />
    </>
  );
}

function ToastStack({
  toasts,
  leaving,
  onDismiss,
  compact,
  position,
}: {
  toasts: Toast[];
  leaving: ReadonlySet<number>;
  onDismiss: (id: number) => void;
  compact: boolean;
  position: string;
}) {
  return (
    <div
      // Announced without stealing focus, so a toast never interrupts typing.
      role="status"
      aria-live="polite"
      className={`pointer-events-none fixed ${position} z-50 flex w-full max-w-sm flex-col gap-2`}
    >
      {toasts.map((toast) => {
        const out = leaving.has(toast.id);
        return (
          <div
            key={toast.id}
            aria-hidden={out || undefined}
            inert={out || undefined}
            data-leaving={out ? "" : undefined}
            className="group/toast grid grid-rows-[1fr] transition-[grid-template-rows,margin] duration-(--motion-base) ease-(--ease-in-out) data-leaving:-mb-2 data-leaving:grid-rows-[0fr]"
          >
            <div className="min-h-0 group-data-leaving/toast:overflow-hidden">
              <div
                className={cn(
                  "animate-[mw-toast-in_var(--motion-slow)_var(--ease-out)_backwards] transition-[opacity,transform] duration-(--motion-base) ease-(--ease-in)",
                  out && "scale-(--motion-pop) opacity-0",
                  compact
                    ? "pointer-events-auto rounded-card border px-4 py-3 text-ui leading-[1.35] font-medium shadow-float"
                    : "pointer-events-auto rounded-[var(--radius-card)] border px-4 py-3 shadow-[var(--shadow-card)]",
                  toneClass[toast.tone],
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex flex-col gap-0.5">
                    <p className={compact ? undefined : "font-sans text-prose font-medium"}>
                      {toast.title}
                    </p>
                    {toast.hint ? (
                      <p className={cn("text-meta text-ink-2", compact && "font-normal")}>
                        {toast.hint}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type={compact ? "button" : undefined}
                    onClick={() => {
                      onDismiss(toast.id);
                    }}
                    aria-label="Dismiss"
                    className={
                      compact
                        ? "-mx-[9px] -my-[5px] px-[9px] py-[5px] text-prose leading-none text-ink-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-accent"
                        : "text-ink-3 hover:text-ink focus-visible:outline-2 focus-visible:outline-accent"
                    }
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
